import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { usePeut } from '../../../auth/usePeut.js';
import { formaterDate, formaterOctets } from '../../../lib/format.js';
import { useLibellesModules } from '../../../lib/modules.js';
import { estClos, formaterJour } from '../../../lib/ouvertures.js';
import type { DossierOuverture } from '../../../types.js';
import formStyles from '../../../styles/form.module.css';
import pageStyles from '../../../styles/page.module.css';
import styles from '../Ouvertures.module.css';

function Ligne({ cle, children }: { cle: string; children: ReactNode }): JSX.Element {
  return (
    <div className={styles.faitsLigne}>
      <dt>{cle}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Ce que le dossier dit de l'offre et du contrat, en lecture ; la modification passe par l'assistant. */
export function OffreContrat({ dossier }: { dossier: DossierOuverture }): JSX.Element {
  const { t } = useTranslation('console');
  const libelles = useLibellesModules();
  const peut = usePeut();
  const { identite, offre, contrat, prereglages } = dossier;
  const aucun = t('ouvertures.fiche.offreContrat.aucun');
  const adresse = [
    identite.adresse.ligne1,
    identite.adresse.ligne2,
    `${identite.adresse.codePostal} ${identite.adresse.ville}`.trim(),
    identite.adresse.pays,
  ]
    .filter((ligne) => ligne.trim() !== '')
    .join(', ');
  const modifiable = peut('console:manage-onboarding') && !estClos(dossier.etat);
  const verrouille = dossier.galleryId !== null;

  const lien = (etape: number): JSX.Element | null =>
    modifiable && !verrouille ? (
      <Link to={`/ouvertures/${dossier.id}/assistant?etape=${etape}`} className="lien-cible">
        {t('ouvertures.fiche.offreContrat.modifier')}
      </Link>
    ) : null;

  return (
    <div className={styles.pile}>
      {modifiable && verrouille ? (
        <p className={formStyles.aide}>{t('ouvertures.fiche.offreContrat.verrouille')}</p>
      ) : null}

      <section className={pageStyles.panneau} aria-labelledby="ouv-identite">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-identite">{t('ouvertures.fiche.offreContrat.identite')}</h2>
          {lien(1)}
        </div>
        <dl className={styles.faits}>
          <Ligne cle={t('ouvertures.fiche.offreContrat.slug')}>
            {identite.slug ? `${identite.slug}.artosera.gallery` : aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.raisonSociale')}>
            {identite.raisonSociale || aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.siren')}>{identite.siren || aucun}</Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.siret')}>{identite.siret || aucun}</Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.tva')}>
            {identite.tvaIntracom || aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.adresse')}>{adresse || aucun}</Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.langue')}>
            {[
              t(`ouvertures.galerie.langues.${identite.langue}`),
              identite.devise,
              identite.fuseau,
            ].join(' · ')}
          </Ligne>
        </dl>
      </section>

      <section className={pageStyles.panneau} aria-labelledby="ouv-offre">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-offre">{t('ouvertures.fiche.offreContrat.offre')}</h2>
          {lien(2)}
        </div>
        <dl className={styles.faits}>
          <Ligne cle={t('ouvertures.fiche.offreContrat.palier')}>
            {t(`galeries.palier.${offre.palier}`)}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.groupe')}>{offre.groupeSlug ?? aucun}</Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.modules')}>
            {offre.modules.length > 0 ? libelles.liste(offre.modules) : aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.addons')}>
            {offre.addons.length > 0
              ? offre.addons
                  .map(
                    (droit) =>
                      `${libelles.libelle(droit.addon)} (${t(`ouvertures.offre.modes.${droit.mode}`)}${
                        droit.jusquAu
                          ? `, ${t('ouvertures.fiche.offreContrat.jusquAu', {
                              date: formaterJour(droit.jusquAu),
                            })}`
                          : ''
                      })`,
                  )
                  .join(', ')
              : aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.services')}>
            {offre.services.length > 0 ? libelles.liste(offre.services) : aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.forfait')}>
            {offre.forfaitReprise.texte || offre.forfaitReprise.montantCentimes !== null
              ? [
                  offre.forfaitReprise.texte,
                  offre.forfaitReprise.montantCentimes === null
                    ? ''
                    : new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'EUR',
                      }).format(offre.forfaitReprise.montantCentimes / 100),
                ]
                  .filter((morceau) => morceau !== '')
                  .join(' · ')
              : aucun}
          </Ligne>
        </dl>
      </section>

      <section className={pageStyles.panneau} aria-labelledby="ouv-contrat">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-contrat">{t('ouvertures.fiche.offreContrat.contrat')}</h2>
          {lien(3)}
        </div>
        <dl className={styles.faits}>
          <Ligne cle={t('ouvertures.fiche.offreContrat.reference')}>
            {contrat.reference || aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.signeLe')}>
            {contrat.signeLe ? formaterJour(contrat.signeLe) : aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.piece')}>
            {contrat.piece
              ? t('ouvertures.contrat.pieceDepose', {
                  date: formaterDate(contrat.piece.deposeLe, ''),
                  par: contrat.piece.par,
                  taille: formaterOctets(contrat.piece.octets),
                })
              : aucun}
          </Ligne>
        </dl>
      </section>

      <section className={pageStyles.panneau} aria-labelledby="ouv-prereglages">
        <div className={pageStyles.sectionTitre}>
          <h2 id="ouv-prereglages">{t('ouvertures.fiche.offreContrat.prereglages')}</h2>
          {lien(5)}
        </div>
        <dl className={styles.faits}>
          <Ligne cle={t('ouvertures.fiche.offreContrat.mentions')}>
            {prereglages.mentionsComplementaires.length > 0
              ? prereglages.mentionsComplementaires.join(' / ')
              : aucun}
          </Ligne>
          <Ligne cle={t('ouvertures.fiche.offreContrat.domaine')}>
            {prereglages.domaine ?? aucun}
          </Ligne>
        </dl>
      </section>
    </div>
  );
}
